"use client";

import React from "react";
import { ShieldCheck, HardHat, ClipboardCheck, Building2, TriangleAlert as AlertTriangle, ArrowRight, FileCheck, MapPin, CircleCheck as CheckCircle2, ExternalLink } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const readingContexts = [{
  icon: Building2,
  title: "Town centre & high-footfall areas",
  description: "Working in central Reading requires strict pedestrian protection, debris netting, fan boards, and precise coordination with local highways for pavement licensing."
}, {
  icon: HardHat,
  title: "Commercial & office developments",
  description: "Multi-storey commercial sites demand robust loading bays, heavy-duty access towers, and structured build phases to keep concurrent trades moving safely."
}, {
  icon: MapPin,
  title: "Mixed residential & narrow streets",
  description: "Tight access lanes and terraced residential roads require careful site logistics, compact footing solutions, and neighbor-conscious erection schedules."
}];
const safetyProtocols = [{
  step: "01",
  title: "Site assessment & survey",
  description: "Thorough evaluation of ground conditions, overhead hazards, public rights of way, and required weight load ratings before any tube hits the ground."
}, {
  step: "02",
  title: "Safe access installation",
  description: "Built by fully qualified scaffolders adhering strictly to TG20:21 compliance, with integrated ladder access towers and fall-prevention barriers."
}, {
  step: "03",
  title: "Handover inspection & tagging",
  description: "Comprehensive pre-use inspection, formal handover certificate issue, and high-visibility Scafftag placement certifying the structure ready for work."
}];
const accreditations = [{
  name: "NASC",
  title: "National Access & Scaffolding Confederation",
  detail: "Audited to industry-leading technical and safety benchmarks."
}, {
  name: "CHAS",
  title: "Contractors Health and Safety Assessment",
  detail: "Demonstrated compliance in occupational health and risk control."
}];
const corePostcodeDistricts = [{
  code: "RG1",
  label: "Reading Town Centre & Newtown"
}, {
  code: "RG2",
  label: "South Reading, Whitley & Green Park"
}, {
  code: "RG4",
  label: "Caversham & Emmer Green"
}, {
  code: "RG30",
  label: "Tilehurst & West Reading"
}, {
  code: "RG31",
  label: "Calcot & Tilehurst West"
}];
const urbanFringeDistricts = [{
  code: "RG5",
  label: "Woodley"
}, {
  code: "RG6",
  label: "Earley & Lower Earley"
}];
const nearbyAreaLinks = [{
  name: "Bracknell",
  to: "/scaffolding-in-bracknell",
  description: "Commercial, industrial, and town development access"
}, {
  name: "Wokingham",
  to: "/scaffolding-in-wokingham",
  description: "Town-centre refurbishment, roofing, and domestic builds"
}, {
  name: "Newbury",
  to: "/scaffolding-in-newbury",
  description: "West Berkshire commercial, residential, and heritage access"
}];
export default function AccessSafety() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="readingaccessandsafety" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 border-border">
            Reading &amp; Thames Valley
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Access planning and safety standards across Reading
          </h2>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            From busy town centre thoroughfares to constrained commercial
            developments, every scaffold is designed for safe pedestrian flow,
            verified handover, and rigorous regulatory compliance.
          </p>
        </div>

        {/* Reading-specific Context Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {readingContexts.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.title} data-index={i} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp}>
                <Card className="h-full border-border bg-card shadow-xs">
                  <CardHeader>
                    <div className="size-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center mb-2">
                      <Icon className="size-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {item.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Safety Process & Accreditations Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mb-16">
          {/* 3-Step Safety Lifecycle */}
          <div className="lg:col-span-7">
            <div className="mb-6">
              <h3 className="text-2xl font-bold tracking-tight text-foreground">
                Our safety and handover protocol
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                Every project follows a transparent three-stage safety verification.
              </p>
            </div>

            <div className="space-y-4">
              {safetyProtocols.map((protocol, i) => <div key={protocol.step} data-index={i} className="flex gap-4 p-5 rounded-lg border border-border bg-card shadow-2xs">
                  <div className="shrink-0 font-mono text-sm font-bold text-primary bg-accent px-2.5 py-1 rounded h-fit">
                    {protocol.step}
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground text-base">
                      {protocol.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                      {protocol.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* Accreditations & Compliance Card */}
          <div className="lg:col-span-5">
            <Card className="border-border bg-card shadow-sm">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm">
                  <ShieldCheck className="size-4" />
                  <span>Verified safety accreditations</span>
                </div>
                <CardTitle className="text-xl font-bold text-card-foreground mt-1">
                  Accredited by industry bodies
                </CardTitle>
                <CardDescription className="text-muted-foreground text-sm">
                  BMF Scaffolding operates under strict safety audits and technical guidelines.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="p-4 rounded-md border border-border bg-accent/30 flex items-start gap-3">
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-foreground text-sm">
                          {acc.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          — {acc.title}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {acc.detail}
                      </p>
                    </div>
                  </div>)}

                <Separator className="my-2" />

                <div className="space-y-2 pt-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <FileCheck className="size-4 text-primary shrink-0" />
                    <span>Full handover certificates issued upon completion</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ClipboardCheck className="size-4 text-primary shrink-0" />
                    <span>Statutory 7-day re-inspections available on demand</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="size-4 text-primary shrink-0" />
                    <span>Public highway licensing coordination handled</span>
                  </div>
                </div>

                <div className="pt-3">
                  <Button asChild className="w-full justify-between" size="lg">
                    <Link to="/ContactRequestQuotePage">
                      <span>Request a site survey in Reading</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Postcode Districts & Coverage Scope */}
        <div className="border border-border rounded-lg bg-card p-6 md:p-8 shadow-xs">
          <div className="max-w-3xl mb-8">
            <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-2">
              <MapPin className="size-4" />
              <span>Reading Postcode Districts &amp; Thames Valley Coverage</span>
            </div>
            <h3 className="text-2xl font-bold tracking-tight text-foreground">
              Core Reading postcodes and immediate coverage boundaries
            </h3>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              We provide rapid scaffolding response across Reading’s core
              postcode districts. Please note that Reading itself does not cover
              “RG1–RG10”, as several RG districts represent distinct towns across
              Berkshire and North Hampshire.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Core & Immediate Districts */}
            <div className="lg:col-span-7 space-y-6">
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  Principal Reading Postcode Districts
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {corePostcodeDistricts.map((item, i) => <div key={item.code} data-index={i} className="p-3 rounded-md border border-border bg-background flex items-center justify-between">
                      <span className="font-mono font-bold text-sm text-foreground">
                        {item.code}
                      </span>
                      <span className="text-xs text-muted-foreground text-right">
                        {item.label}
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="p-4 rounded-md border border-border bg-accent/30">
                <h4 className="text-sm font-semibold text-foreground mb-1">
                  Immediate Reading Urban-Area Coverage
                </h4>
                <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                  Distinguished from the core Reading districts, RG5 and RG6 form
                  the immediate urban fringe:
                </p>
                <div className="flex flex-wrap gap-2">
                  {urbanFringeDistricts.map((item, i) => <Badge key={item.code} data-index={i} variant="outline" className="border-border bg-background py-1 px-2.5 text-xs text-foreground">
                      <span className="font-mono font-semibold mr-1.5 text-primary">
                        {item.code}
                      </span>
                      <span>{item.label}</span>
                    </Badge>)}
                </div>
              </div>
            </div>

            {/* Nearby Operational Areas */}
            <div className="lg:col-span-5 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-border pt-6 lg:pt-0 lg:pl-8">
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">
                  Nearby Thames Valley Service Hubs
                </h4>
                <p className="text-xs text-muted-foreground mb-4">
                  For projects situated outside Reading borough, visit our dedicated
                  regional service pages:
                </p>

                <div className="space-y-3">
                  {nearbyAreaLinks.map((area, i) => <Link key={area.name} to={area.to} data-index={i} className="group block p-3 rounded-md border border-border bg-background hover:border-primary/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                          {area.name}
                        </span>
                        <ExternalLink className="size-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {area.description}
                      </p>
                    </Link>)}
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-border">
                <Button asChild variant="outline" className="w-full justify-between">
                  <Link to="#">
                    <span>View all areas</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}